/* Сессия: текущий пользователь, выбранная точка, справочники точки, права, живые обновления (SSE). */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  VERTICAL_LABELS,
  can as canRole,
  setCurrencySign,
  type DomainCtx,
  type Me,
  type Permission,
  type Role,
  type Vertical,
} from "@okno/shared";
import { api } from "./api";
import type { Board, Boot } from "./types";

const LOC_KEY = "okno-location";
const CURRENCY: Record<string, string> = { KZT: "₸", UZS: "сум", KGS: "сом", RUB: "₽" };

export interface Session {
  me: Me;
  role: Role;
  locationId: string;
  boot: Boot;
  vertical: Vertical;
  labels: (typeof VERTICAL_LABELS)[Vertical];
  can: (p: Permission) => boolean;
  setLocation: (id: string) => void;
  logout: () => Promise<void>;
}

const Ctx = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("useSession вне SessionProvider");
  return s;
}

/** Ключ запросов, привязанных к точке: любые изменения точки инвалидируют их разом. */
export function locKey(locationId: string, ...rest: unknown[]): unknown[] {
  return ["loc", locationId, ...rest];
}

function readLoc(): string | null {
  try {
    return localStorage.getItem(LOC_KEY);
  } catch {
    return null;
  }
}

export function SessionProvider({ me, children, onLogout }: { me: Me; children: ReactNode; onLogout: () => void }) {
  const [locationId, setLocationId] = useState<string>(() => {
    const saved = readLoc();
    return me.locations.find((l) => l.id === saved)?.id || me.locations[0]?.id || "";
  });

  const boot = useQuery({
    queryKey: locKey(locationId, "boot"),
    queryFn: () => api.get<Boot>("/locations/" + locationId + "/"),
    enabled: !!locationId,
  });

  useLiveUpdates(locationId);

  useEffect(() => {
    if (boot.data) setCurrencySign(CURRENCY[boot.data.location.currency] || boot.data.location.currency);
  }, [boot.data]);

  const setLocation = useCallback((id: string) => {
    try {
      localStorage.setItem(LOC_KEY, id);
    } catch {
      /* без localStorage точка не запомнится — не критично */
    }
    setLocationId(id);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => undefined);
    onLogout();
  }, [onLogout]);

  const value = useMemo<Session | null>(() => {
    if (!boot.data) return null;
    const vertical = boot.data.location.vertical;
    return {
      me,
      role: me.role,
      locationId,
      boot: boot.data,
      vertical,
      labels: VERTICAL_LABELS[vertical],
      can: (p: Permission) => canRole(me.role, p),
      setLocation,
      logout,
    };
  }, [boot.data, me, locationId, setLocation, logout]);

  if (!locationId) return <FullScreenMessage title="Нет доступных точек" text="Обратитесь к владельцу, чтобы вам открыли доступ." />;
  if (boot.isError) return <FullScreenMessage title="Не удалось загрузить точку" text="Обновите страницу или попробуйте позже." />;
  if (!value) return <FullScreenMessage title="Загрузка…" text="" />;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function FullScreenMessage({ title, text }: { title: string; text: string }) {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div className="empty" style={{ maxWidth: 420 }}>
        <strong>{title}</strong>
        {text ? <span>{text}</span> : null}
      </div>
    </div>
  );
}

/** Подписка на изменения точки (SSE): при любом событии перезапрашиваем данные точки. */
function useLiveUpdates(locationId: string): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (!locationId || typeof EventSource === "undefined") return;
    const es = new EventSource("/api/locations/" + locationId + "/events");
    let t: ReturnType<typeof setTimeout> | null = null;
    es.onmessage = () => {
      // пачку событий (например, рассылка) схлопываем в одно обновление
      if (t) return;
      t = setTimeout(() => {
        t = null;
        void qc.invalidateQueries({ queryKey: ["loc", locationId] });
      }, 300);
    };
    return () => {
      es.close();
      if (t) clearTimeout(t);
    };
  }, [locationId, qc]);
}

/** Данные окна времени: заказы, клиенты и авто из них, блокировки, погода. */
export function useBoard(from: number, to: number) {
  const { locationId } = useSession();
  return useQuery({
    queryKey: locKey(locationId, "board", from, to),
    queryFn: () => api.get<Board>("/locations/" + locationId + "/board", { from, to }),
    placeholderData: (prev) => prev,
  });
}

/** Контекст для функций @okno/shared (conflict, gaps, idleWindows…). */
export function useDomainCtx(board: Board | undefined): DomainCtx | null {
  const { boot } = useSession();
  return useMemo(() => {
    if (!board) return null;
    return {
      vertical: boot.location.vertical,
      location: boot.location,
      bays: boot.bays,
      services: boot.services,
      orders: board.orders,
      blocks: board.blocks,
      weather: board.weather,
    };
  }, [board, boot]);
}

/** Текущее время, обновляется раз в 30 секунд — для линии «сейчас» и таймеров смены. */
export function useNow(intervalMs = 30000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
